import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FormControl } from "react-bootstrap";

import {
	canAppendDurationDigit,
	DURATION_DIGIT_LIMIT,
	durationDigitsToMinutes,
	formatDurationDigitsForDisplay,
	minutesToDurationDigits,
	parseDurationPaste,
} from "utils/timeBalance";

const DurationInput = ({
	initialMinutes = 0,
	minutes,
	onChange,
	isInvalid,
	className,
	onKeyDown,
	autoFocus = false,
}) => {
	const { t } = useTranslation();
	const inputRef = useRef(null);
	const [digits, setDigits] = useState(() =>
		initialMinutes > 0 ? minutesToDurationDigits(initialMinutes) : ""
	);

	useEffect(() => {
		if (minutes === undefined) return;
		setDigits((currentDigits) => {
			const currentMinutes = durationDigitsToMinutes(currentDigits);
			if (currentMinutes === minutes) return currentDigits;
			return minutes > 0 ? minutesToDurationDigits(minutes) : "";
		});
	}, [minutes]);

	const displayValue = digits ? formatDurationDigitsForDisplay(digits) || "" : "";

	const applyDigits = (nextDigits) => {
		setDigits(nextDigits);
		onChange(durationDigitsToMinutes(nextDigits));
	};

	const keepCaretAtEnd = () => {
		requestAnimationFrame(() => {
			const input = inputRef.current;
			if (!input) return;
			const end = input.value.length;
			input.setSelectionRange(end, end);
		});
	};

	const appendDigit = (digit) => {
		if (!canAppendDurationDigit(digits, digit)) return;
		applyDigits(`${digits}${digit}`.slice(0, DURATION_DIGIT_LIMIT));
		keepCaretAtEnd();
	};

	const handleKeyDown = (event) => {
		onKeyDown?.(event);
		if (event.defaultPrevented) return;

		if (event.key >= "0" && event.key <= "9") {
			event.preventDefault();
			appendDigit(event.key);
			return;
		}

		if (event.key === "Backspace") {
			event.preventDefault();
			applyDigits(digits.slice(0, -1));
			keepCaretAtEnd();
			return;
		}

		if (event.key === "Delete") {
			event.preventDefault();
			applyDigits("");
		}
	};

	const handlePaste = (event) => {
		const pasted = parseDurationPaste(event.clipboardData.getData("text"));
		if (pasted === null) return;

		event.preventDefault();
		applyDigits(pasted);
		keepCaretAtEnd();
	};

	const handleChange = (event) => {
		// Fallback para teclados móviles.
		const incoming = event.target.value.replace(/\D/g, "").slice(0, DURATION_DIGIT_LIMIT);
		if (!incoming) {
			applyDigits("");
			return;
		}

		if (incoming.length <= digits.length) {
			applyDigits(incoming);
			keepCaretAtEnd();
			return;
		}

		let next = digits;
		for (const char of incoming.slice(digits.length)) {
			if (!canAppendDurationDigit(next, char)) break;
			next = `${next}${char}`.slice(0, DURATION_DIGIT_LIMIT);
		}
		applyDigits(next);
		keepCaretAtEnd();
	};

	const handleSelect = () => keepCaretAtEnd();
	const handleClick = () => keepCaretAtEnd();

	return (
		<FormControl
			ref={inputRef}
			type="text"
			inputMode="numeric"
			autoComplete="off"
			autoFocus={autoFocus}
			spellCheck={false}
			className={className}
			value={displayValue}
			onChange={handleChange}
			onKeyDown={handleKeyDown}
			onPaste={handlePaste}
			onSelect={handleSelect}
			onClick={handleClick}
			placeholder={t("times.balance.manualEntry.valuePlaceholder")}
			isInvalid={isInvalid}
		/>
	);
};

export default DurationInput;
